import React, { useEffect, useRef, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../i18n/translations';

const Roadmap = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const milestones = t.roadmap.milestones;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error(t.toasts.nameRequired);
      return;
    }
    
    if (!email.trim()) {
      toast.error(t.toasts.emailRequired);
      return;
    }
    
    if (!email.includes('@')) {
      toast.error(t.toasts.emailInvalid);
      return;
    }
    
    toast.success(t.toasts.subscribeSuccess);
    setEmail('');
    setName('');
  };

  return (
    <section
      id="roadmap"
      ref={sectionRef}
      className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8"
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-secondary text-sm font-semibold tracking-widest uppercase">{t.roadmap.subtitle}</span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-2 mb-6">
            {t.roadmap.title.split('—')[0]}— <span className="text-glow-fire" style={{ color: 'hsl(6 55% 45%)' }}>{t.roadmap.title.split('—')[1]}</span>
          </h2>
        </div>

        {/* Timeline */}
        <div className="relative mb-16">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-secondary to-accent hidden md:block" />

          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div
                key={index}
                className={`relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <Card className="md:ml-20 p-6 bg-card/30 backdrop-blur border-border/50 hover:border-primary/30 transition-all group">
                  {/* Timeline Dot */}
                  <div className="absolute -left-2 top-6 hidden md:block">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      milestone.status === language === 'en' ? 'In Progress' : 'En Progreso'
                        ? 'bg-primary border-primary shadow-glow'
                        : 'bg-background border-border'
                    } group-hover:scale-125 transition-transform`} />
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-semibold text-primary">{milestone.period}</span>
                        {milestone.status === (language === 'en' ? 'In Progress' : 'En Progreso') && (
                          <CheckCircle2 className="h-5 w-5 text-primary" />
                        )}
                      </div>
                      <h3 className="text-xl font-bold mb-1 text-foreground group-hover:text-primary transition-colors">
                        {milestone.title}
                      </h3>
                      <p className="text-muted-foreground">{milestone.description}</p>
                    </div>
                    <div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        milestone.status === (language === 'en' ? 'In Progress' : 'En Progreso')
                          ? 'bg-primary/20 text-primary'
                          : milestone.status === (language === 'en' ? 'Coming Soon' : 'Próximamente')
                          ? 'bg-accent/20 text-accent'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {milestone.status}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>

        {/* Mailing List */}
        <Card className="max-w-2xl mx-auto p-8 bg-card/50 backdrop-blur border-border/50">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold mb-2 text-primary">{t.roadmap.mailingList.title}</h3>
            <p className="text-muted-foreground">
              {t.roadmap.mailingList.description}
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="text"
                placeholder={t.roadmap.mailingList.namePlaceholder}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-background/50 border-border/50 focus:border-primary"
              />
            </div>
            <div>
              <Input
                type="email"
                placeholder={t.roadmap.mailingList.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-background/50 border-border/50 focus:border-primary"
              />
            </div>
            <Button
              type="submit"
              className="w-full divine-glow bg-primary text-primary-foreground hover:bg-primary/90"
              size="lg"
            >
              {t.roadmap.mailingList.subscribe}
            </Button>
          </form>
        </Card>
      </div>
    </section>
  );
};

export default Roadmap;
